// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Flashbar, {
  type FlashbarProps,
} from "@cloudscape-design/components/flashbar";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import { useAuth } from "./CognitoAuthProvider";

export default function LoginPage(): React.ReactElement {
  const { isAuthenticated, loading, error, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/enroll");
    }
  }, [isAuthenticated, navigate]);

  const flashbarItems: FlashbarProps.MessageDefinition[] = error
    ? [{ type: "error", content: error, dismissible: true, id: "auth-error" }]
    : [];

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "2rem",
      }}
    >
      <div style={{ maxWidth: 480, width: "100%" }}>
        <SpaceBetween size="l">
          {flashbarItems.length > 0 && <Flashbar items={flashbarItems} />}
          <Container>
            <SpaceBetween size="l" alignItems="center">
              <img
                src="/yubipca.png"
                alt="YubiKey PIV Enrollment"
                style={{ width: 120, height: 120 }}
              />
              <Box variant="h1" textAlign="center">
                YubiKey PIV Enrollment
              </Box>
              <Box variant="p" textAlign="center" color="text-body-secondary">
                Enroll your YubiKey for PIV certificate issuance. Sign in with
                your passkey to get started.
              </Box>
              {loading ? (
                <StatusIndicator type="loading">
                  Signing you in…
                </StatusIndicator>
              ) : (
                <Button variant="primary" onClick={login} ariaLabel="Sign in to YubiKey PIV Enrollment">
                  Sign in
                </Button>
              )}
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </div>
    </div>
  );
}
